import { useCallback } from 'react'
import { toast } from 'react-toastify'

import { useGetDataCollectionConfigsQuery } from '../../app/apiSliceConfigStudioApi'
import {
  SYSTEM_DISCOVERY_APP_NAME,
  findSystemDiscoveryRule,
  updateSystemDiscoveryRuleWithExclusions,
} from './systemDiscoveryUtils'
import useManageServerTestCasesAndConfigs from './useManageServerTestCasesAndConfigs'

interface UseSyncSystemDiscoveryExclusionsReturn {
  syncExclusions: () => Promise<void>
  isSyncing: boolean
  isSystemDiscoveryEnabled: boolean
}

/**
 * Hook to sync System Discovery exclusions with all tracked app rules.
 * Updates the System Discovery rule to exclude all other tracked applications.
 */
const useSyncSystemDiscoveryExclusions = (): UseSyncSystemDiscoveryExclusionsReturn => {
  const { data: ruleData } = useGetDataCollectionConfigsQuery()
  const { updateRules, isUploadingRules } = useManageServerTestCasesAndConfigs()

  const systemDiscoveryRule = findSystemDiscoveryRule(ruleData?.rules ?? [])
  const isSystemDiscoveryEnabled = Boolean(systemDiscoveryRule)

  const syncExclusions = useCallback(async () => {
    if (!ruleData?.rules) {
      toast.error('No rules data available')
      return
    }

    if (!systemDiscoveryRule) {
      toast.error(`${SYSTEM_DISCOVERY_APP_NAME} is not enabled`)
      return
    }

    try {
      // Update the System Discovery rule with new exclusions
      const updatedRule = updateSystemDiscoveryRuleWithExclusions(
        systemDiscoveryRule,
        ruleData.rules,
      )

      // Update rules in the database
      await updateRules([updatedRule])

      toast.success('System Discovery exclusions synced successfully')
    } catch (error) {
      toast.error('Failed to sync System Discovery exclusions')
      console.error('Error syncing System Discovery exclusions:', error)
    }
  }, [ruleData, systemDiscoveryRule, updateRules])

  return {
    syncExclusions,
    isSyncing: isUploadingRules,
    isSystemDiscoveryEnabled,
  }
}

export default useSyncSystemDiscoveryExclusions

